/*!
 * Clock In Cheeze — Season 1 3D preloader (Three.js)
 *
 * One Season 1 product image is rolled per page load (the roll itself
 * happens inline in snippets/cic-preloader.liquid so the matching
 * background paints with the overlay). Here that image is mounted as a
 * back-to-back pair of planes and eased through a near-full spin, with
 * "SEASON 1" beneath it built as genuinely extruded 3D type — a stack of
 * layered glyph planes with a lit face and accent-coloured sides, so it
 * has real depth that parallaxes as it sways. Soft studio lighting
 * throughout; fades out once the page has finished loading.
 *
 * Config (set BEFORE this script tag via window.CIC_PRELOADER_CONFIG):
 *   images    {array}   [{ url, bg, bgMid, bgEdge, accent, ink }, ...]
 *   pick      {object}  the entry already rolled by the inline picker
 *   season    {string}  text extruded below the product (default 'SEASON 1')
 *   once      {boolean} only show once per browser session (default true)
 *   minTime   {number}  minimum ms the preloader stays visible (default 2750)
 *   maxTime   {number}  safety fallback: force-hide after this many ms (default 6000)
 */
(function () {
  'use strict';

  var config = window.CIC_PRELOADER_CONFIG || {};
  var IMAGES = config.images || [];

  // The inline picker in the snippet normally rolls this and themes the
  // overlay to match. Falling back to a roll here keeps the preloader
  // working if that block ever gets stripped (e.g. by a minifier).
  var PICK = config.pick ||
    (IMAGES.length ? IMAGES[Math.floor(Math.random() * IMAGES.length)] : null) ||
    (config.logoUrl ? { url: config.logoUrl } : null);

  var IMAGE_URL = PICK && PICK.url;
  var ACCENT = (PICK && PICK.accent) || '#d3231e';
  var INK = (PICK && PICK.ink) || '20, 20, 26';
  var SEASON_TEXT = config.season || 'SEASON 1';
  var ONCE_PER_SESSION = config.once !== false; // default true
  var MIN_VISIBLE_MS = typeof config.minTime === 'number' ? config.minTime : 2750;
  var MAX_VISIBLE_MS = typeof config.maxTime === 'number' ? config.maxTime : 6000;
  var STORAGE_KEY = 'cicPreloaderShown';

  var root = document.getElementById('cic-preloader');
  if (!root || !IMAGE_URL) return;

  // Skip entirely if we've already shown it this session.
  if (ONCE_PER_SESSION) {
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) {
        root.parentNode.removeChild(root);
        return;
      }
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (e) { /* storage unavailable, just continue showing it */ }
  }

  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var holder = document.getElementById('cic-canvas-holder');
  var barFill = document.getElementById('cic-bar-fill');
  var stage = root.querySelector('.cic-preloader__stage');
  var particleCanvas = document.getElementById('cic-particles');

  var startTime = Date.now();
  var hidden = false;
  var rafId = null;
  var renderer, scene, camera, productGroup, texture, seasonGroup;
  var seasonMaterials = [];
  var progress = 0;
  var progressTarget = 0.12;
  var sceneStartTime = null;

  // Product spin: eases from 0 to a near-full turn (not exactly 360°, so the
  // motion actually reads rather than looking like it never moved) and
  // settles. Slow and graceful on purpose — the preloader's minimum visible
  // time (below) is stretched to match, so it never fades out mid-spin.
  var ROTATION_TURNS = 0.94;
  var ROTATION_DURATION = 3200;
  var ROTATION_SETTLE_BUFFER = 200; // brief pause once settled, before it's allowed to fade

  // "SEASON 1": pops in beneath the product partway through the spin,
  // then holds with a gentle idle sway that shows off the extrusion.
  var TEXT_DELAY = ROTATION_DURATION * 0.34;
  var TEXT_DURATION = 650;
  var TEXT_LAYERS = 22; // glyph planes stacked back along z to form the extrusion

  function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
  }
  function easeOutCubic(x) {
    return 1 - Math.pow(1 - x, 3);
  }
  function easeOutBack(x) {
    var c1 = 1.70158, c3 = c1 + 1;
    return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
  }

  // ── Colour helpers ────────────────────────────────────────────────
  // Everything visual is derived from the rolled image's accent + ink, so
  // the type and particles belong to the same palette as the background.
  function parseColor(input) {
    if (!input) return { r: 20, g: 20, b: 26 };
    var s = String(input).trim();
    if (s.charAt(0) === '#') {
      if (s.length === 4) {
        return {
          r: parseInt(s[1] + s[1], 16),
          g: parseInt(s[2] + s[2], 16),
          b: parseInt(s[3] + s[3], 16)
        };
      }
      return {
        r: parseInt(s.substr(1, 2), 16),
        g: parseInt(s.substr(3, 2), 16),
        b: parseInt(s.substr(5, 2), 16)
      };
    }
    var parts = s.replace(/rgba?\(|\)/g, '').split(',');
    return {
      r: parseInt(parts[0], 10) || 0,
      g: parseInt(parts[1], 10) || 0,
      b: parseInt(parts[2], 10) || 0
    };
  }
  function rgbaOf(c, a) {
    return 'rgba(' + c.r + ',' + c.g + ',' + c.b + ',' + a + ')';
  }
  function shade(c, amount) {
    // amount > 0 lightens toward white, < 0 darkens toward black
    var t = amount > 0 ? 255 : 0;
    var k = Math.abs(amount);
    return {
      r: Math.round(c.r + (t - c.r) * k),
      g: Math.round(c.g + (t - c.g) * k),
      b: Math.round(c.b + (t - c.b) * k)
    };
  }
  function hexOf(c) {
    return '#' + [c.r, c.g, c.b].map(function (v) {
      return ('0' + Math.max(0, Math.min(255, v)).toString(16)).slice(-2);
    }).join('');
  }

  function relativeLuminance(c) {
    var ch = [c.r, c.g, c.b].map(function (v) {
      v = v / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  }
  function contrastRatio(a, b) {
    var la = relativeLuminance(a), lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }

  var inkColor = parseColor(INK);
  var accentColor = parseColor(ACCENT);
  var bgColor = parseColor((PICK && PICK.bg) || '#f0f1f3');

  // The extruded sides are drawn in the product's accent, but a light
  // accent (the yellow lockup, say) barely separates from a near-white
  // background and the depth stops reading. Darken until it clears 3:1
  // against the background rather than trusting one fixed offset to suit
  // every palette — including whichever colours get added later.
  var sideColor = (function () {
    var amount = -0.12;
    var candidate = shade(accentColor, amount);
    while (contrastRatio(candidate, bgColor) < 3 && amount > -0.62) {
      amount -= 0.05;
      candidate = shade(accentColor, amount);
    }
    return candidate;
  })();

  // Floating particles: the palette's ink with a few accent sparks.
  var PARTICLE_COLORS = [
    rgbaOf(inkColor, 1),
    rgbaOf(shade(inkColor, 0.35), 1),
    rgbaOf(shade(inkColor, 0.55), 1),
    rgbaOf(accentColor, 1),
    rgbaOf(shade(accentColor, 0.25), 1)
  ];
  var PARTICLE_COUNT = 42;
  var particleCtx = null;
  var particles = [];
  var particleRafId = null;
  var particleLastTime = null;
  var particleDpr = 1;

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) {
      return false;
    }
  }

  function setBar(pct) {
    if (barFill) barFill.style.width = (pct * 100).toFixed(1) + '%';
  }

  // Draws the season text twice onto canvases: one "face" pass (the lit
  // front of the letters — an ink gradient) and one "side" pass (the same
  // silhouette in the product's accent, which becomes the extruded depth).
  // Both are mapped onto the stacked planes that make up the 3D type, so
  // the depth is real geometry rather than a painted-on shadow.
  function createSeasonTextures() {
    // 2048 wide so the glyph edges stay sharp: on a phone the type spans
    // most of the viewport, which is ~760 device pixels — a 1024 texture
    // lands near 1:1 there and softens exactly when it's largest.
    var w = 2048, h = 600;

    function paint(mode) {
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d');

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '900 304px Arial, "Helvetica Neue", sans-serif';
      try { ctx.letterSpacing = '10px'; } catch (e) { /* older browsers: ignored */ }
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;

      var cx = w / 2, cy = h / 2;

      // The two passes must trace the SAME silhouette — they're the front
      // and the sides of one extruded shape. Stroking the sides fatter
      // than the face just halos 21 oversized dark copies out around every
      // letter and closes up the counters. Both get a hairline stroke only
      // (to seal the seams between stacked layers), with the face a touch
      // wider so no side can peek around the front of it.
      if (mode === 'side') {
        // Kept in the product's own accent rather than crushed toward
        // black: the depth needs to read as a separate colour from the
        // face, otherwise face and sides merge into one dark smudge.
        ctx.fillStyle = hexOf(sideColor);
        ctx.strokeStyle = hexOf(sideColor);
        ctx.lineWidth = 5;
        ctx.strokeText(SEASON_TEXT, cx, cy);
        ctx.fillText(SEASON_TEXT, cx, cy);
      } else {
        // Hairline edge, dark enough to hold the face apart from the
        // accent sides without thickening the letterforms.
        ctx.lineWidth = 7;
        ctx.strokeStyle = hexOf(shade(inkColor, -0.3));
        ctx.strokeText(SEASON_TEXT, cx, cy);

        // Ink fill, lifted off pure black and carrying a brighter sweep
        // through the middle so the letterforms have some modelling.
        // Still ~13:1 against every background in the set.
        var grad = ctx.createLinearGradient(cx - 840, cy - 200, cx + 840, cy + 200);
        grad.addColorStop(0, hexOf(shade(inkColor, 0.1)));
        grad.addColorStop(0.42, hexOf(shade(inkColor, 0.18)));
        grad.addColorStop(0.5, hexOf(shade(inkColor, 0.3)));
        grad.addColorStop(0.58, hexOf(shade(inkColor, 0.18)));
        grad.addColorStop(1, hexOf(shade(inkColor, 0.1)));
        ctx.fillStyle = grad;
        ctx.fillText(SEASON_TEXT, cx, cy);
      }

      var tex = new THREE.CanvasTexture(canvas);
      tex.encoding = THREE.sRGBEncoding;
      tex.anisotropy = 4;
      return tex;
    }

    return { face: paint('face'), side: paint('side'), aspect: w / h };
  }

  function sizeParticleCanvas() {
    if (!particleCanvas) return;
    particleDpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = window.innerWidth;
    var h = window.innerHeight;
    particleCanvas.width = Math.max(1, Math.floor(w * particleDpr));
    particleCanvas.height = Math.max(1, Math.floor(h * particleDpr));
    particleCanvas.style.width = w + 'px';
    particleCanvas.style.height = h + 'px';
  }

  function makeParticle(spawnAtBottom) {
    var w = window.innerWidth;
    var h = window.innerHeight;
    return {
      baseX: Math.random() * w,
      y: spawnAtBottom ? h + 20 + Math.random() * 60 : Math.random() * h,
      speed: 12 + Math.random() * 20, // px/sec upward
      wobbleAmp: 6 + Math.random() * 20,
      wobbleFreq: 0.3 + Math.random() * 0.5,
      phase: Math.random() * Math.PI * 2,
      size: 1.1 + Math.random() * 2.3,
      color: PARTICLE_COLORS[(Math.random() * PARTICLE_COLORS.length) | 0],
      alpha: 0.12 + Math.random() * 0.3
    };
  }

  function initParticles() {
    if (!particleCanvas || prefersReducedMotion) return;
    particleCtx = particleCanvas.getContext('2d');
    if (!particleCtx) return;

    sizeParticleCanvas();
    particles = [];
    for (var i = 0; i < PARTICLE_COUNT; i++) {
      particles.push(makeParticle(false));
    }
    particleRafId = requestAnimationFrame(animateParticles);
  }

  function animateParticles(now) {
    particleRafId = requestAnimationFrame(animateParticles);
    if (!particleCtx) return;

    var delta = particleLastTime ? Math.min((now - particleLastTime) / 1000, 0.1) : 0;
    particleLastTime = now;

    var w = particleCanvas.width;
    var h = particleCanvas.height;
    particleCtx.clearRect(0, 0, w, h);
    particleCtx.save();
    particleCtx.scale(particleDpr, particleDpr);

    var tSec = now * 0.001;
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      p.y -= p.speed * delta;
      if (p.y < -20) {
        particles[i] = makeParticle(true);
        continue;
      }
      var drawX = p.baseX + Math.sin(tSec * p.wobbleFreq + p.phase) * p.wobbleAmp;

      particleCtx.beginPath();
      particleCtx.globalAlpha = p.alpha;
      particleCtx.fillStyle = p.color;
      particleCtx.shadowBlur = 5;
      particleCtx.shadowColor = p.color;
      particleCtx.arc(drawX, p.y, p.size, 0, Math.PI * 2);
      particleCtx.fill();
    }

    particleCtx.restore();
  }

  function stopParticles() {
    if (particleRafId) cancelAnimationFrame(particleRafId);
    particleRafId = null;
    if (particleCtx && particleCanvas) {
      particleCtx.clearRect(0, 0, particleCanvas.width, particleCanvas.height);
    }
  }

  // Bounds of the product + type stack in world units, used to frame the
  // camera. Recomputed only when the scene is built; the camera itself is
  // refitted on every resize so the same stack fits any viewport.
  var content = { width: 0, top: 0, bottom: 0 };
  var seasonBaseY = 0;

  function initScene(aspect) {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(stage.clientWidth || 320, stage.clientHeight || 320);
    renderer.outputEncoding = THREE.sRGBEncoding;
    holder.appendChild(renderer.domElement);

    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);

    // Studio lighting: soft ambient + key + cool fill + subtle rim
    scene.add(new THREE.AmbientLight(0x404040, 0.7));

    var key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(2.5, 3, 4);
    scene.add(key);

    var fill = new THREE.DirectionalLight(0x6a8dff, 0.35);
    fill.position.set(-3, -1, 2);
    scene.add(fill);

    var rim = new THREE.DirectionalLight(0xffffff, 0.5);
    rim.position.set(-1.5, 1.5, -3);
    scene.add(rim);

    // Product: a flat plane sized to the image's own aspect ratio, mirrored
    // by a second plane facing the other way. Both are front-facing, so the
    // back half of the spin shows the artwork the right way round instead of
    // a mirrored reflection of it — which matters for the banner images,
    // where the type would otherwise read backwards.
    var cardWidth = 2.5;
    var cardHeight = cardWidth / aspect;

    // Unlit on purpose: the whole point of the per-image palette is that
    // the artwork's own background is the overlay's background, and a lit
    // material would tint the image away from the CSS colour behind it and
    // leave a visible rectangle. Basic material renders the texture at its
    // true values, so a banner's baked-in background dissolves exactly.
    var geometry = new THREE.PlaneGeometry(cardWidth, cardHeight);
    var materialOpts = {
      map: texture,
      transparent: true,
      alphaTest: 0.05,
      side: THREE.FrontSide
    };

    productGroup = new THREE.Group();
    var front = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial(materialOpts));
    front.position.z = 0.004;
    var back = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial(materialOpts));
    back.rotation.y = Math.PI;
    back.position.z = -0.004;
    productGroup.add(front, back);
    scene.add(productGroup);

    // "SEASON 1" as real extruded geometry: one lit face plane at the
    // front and TEXT_LAYERS-1 accent-shaded silhouettes stepping back
    // along z. Render order is pinned back-to-front so the stack composites
    // correctly no matter how the renderer chooses to sort it.
    var seasonTex = createSeasonTextures();
    var seasonWidth = cardWidth * 0.86;
    var seasonHeight = seasonWidth / seasonTex.aspect;
    var seasonGap = cardWidth * 0.075;
    var seasonDepth = seasonHeight * 0.42;
    seasonBaseY = -(cardHeight / 2) - seasonGap - seasonHeight / 2;

    var seasonGeometry = new THREE.PlaneGeometry(seasonWidth, seasonHeight);
    var faceMaterial = new THREE.MeshStandardMaterial({
      map: seasonTex.face,
      transparent: true,
      opacity: 0,
      alphaTest: 0.02,
      depthWrite: false,
      roughness: 0.34,
      metalness: 0.3
    });
    var sideMaterial = new THREE.MeshBasicMaterial({
      map: seasonTex.side,
      transparent: true,
      opacity: 0,
      alphaTest: 0.02,
      depthWrite: false
    });
    seasonMaterials = [faceMaterial, sideMaterial];

    seasonGroup = new THREE.Group();
    for (var i = TEXT_LAYERS - 1; i >= 0; i--) {
      var isFace = i === 0;
      var layer = new THREE.Mesh(
        seasonGeometry,
        isFace ? faceMaterial : sideMaterial
      );
      layer.position.z = -(i / (TEXT_LAYERS - 1)) * seasonDepth;
      layer.renderOrder = TEXT_LAYERS - i; // farthest slice drawn first
      seasonGroup.add(layer);
    }
    seasonGroup.position.set(0, seasonBaseY, 0);
    seasonGroup.rotation.x = -0.1; // slight tilt so the depth is visible head-on
    seasonGroup.scale.setScalar(0.0001);
    scene.add(seasonGroup);

    content.width = cardWidth;
    content.top = cardHeight / 2;
    content.bottom = seasonBaseY - seasonHeight / 2;

    fitCamera();

    if (prefersReducedMotion) {
      productGroup.rotation.y = -0.35;
      seasonGroup.scale.setScalar(1);
      seasonGroup.rotation.y = -0.18;
      faceMaterial.opacity = 1;
      sideMaterial.opacity = 1;
    }

    animate();
  }

  // Frames the product + type stack to whatever box the stage currently
  // is, fitting on BOTH axes and taking whichever distance is further
  // back. Height alone isn't enough: the banner artwork is roughly 2:1,
  // so on a narrow phone it's the width that decides the framing.
  function fitCamera() {
    if (!camera || !stage) return;
    var w = stage.clientWidth || 320;
    var h = stage.clientHeight || 320;

    camera.aspect = w / h;

    var stackHeight = content.top - content.bottom;
    var stackCenterY = (content.top + content.bottom) / 2;
    var margin = w < 480 ? 1.06 : 1.12;

    var halfFovY = (camera.fov * Math.PI / 180) / 2;
    var halfFovX = Math.atan(Math.tan(halfFovY) * camera.aspect);
    var distForHeight = (stackHeight * margin / 2) / Math.tan(halfFovY);
    var distForWidth = (content.width * margin / 2) / Math.tan(halfFovX);

    camera.position.set(0, stackCenterY + 0.12, Math.max(distForHeight, distForWidth));
    camera.lookAt(0, stackCenterY, 0);
    camera.updateProjectionMatrix();
  }

  function animate() {
    if (hidden) return;
    rafId = requestAnimationFrame(animate);

    var now = Date.now();
    if (sceneStartTime === null) sceneStartTime = now;
    var sceneElapsed = now - sceneStartTime;
    var t = now * 0.001;

    if (!prefersReducedMotion && productGroup) {
      // Ease from 0 to a near-full turn, then hold — so it always reads as
      // "it spun around" rather than looking arbitrarily interrupted.
      var rotFrac = easeOutCubic(clamp01(sceneElapsed / ROTATION_DURATION));
      productGroup.rotation.y = rotFrac * ROTATION_TURNS * Math.PI * 2;
      productGroup.position.y = Math.sin(t * 1.4) * 0.05;
    }

    if (seasonGroup && !prefersReducedMotion) {
      var textElapsed = sceneElapsed - TEXT_DELAY;
      var textFrac = clamp01(textElapsed / TEXT_DURATION);
      var appear = textElapsed <= 0 ? 0 : easeOutBack(textFrac);

      seasonGroup.visible = textElapsed > 0;
      seasonGroup.scale.setScalar(Math.max(appear, 0.0001));

      var opacity = clamp01(textFrac * 1.4);
      seasonMaterials[0].opacity = opacity;
      seasonMaterials[1].opacity = opacity;

      // Swings in off-axis so the extruded sides sweep past as it lands,
      // then settles into a slow sway that keeps the depth readable.
      var entrance = (1 - clamp01(textFrac)) * -0.75;
      var idleSway = textFrac >= 1 ? Math.sin(t * 0.55) * 0.22 : 0;
      seasonGroup.rotation.y = entrance + idleSway;

      var slideIn = (1 - clamp01(textFrac)) * 0.35;
      var idleBob = textFrac >= 1 ? Math.sin(t * 1.1 + 1.5) * 0.03 : 0;
      seasonGroup.position.y = seasonBaseY - slideIn + idleBob;
      seasonGroup.rotation.z = textFrac >= 1 ? Math.sin(t * 0.6) * 0.035 : 0;
    }

    // Ease the fake progress bar toward its target, never quite reaching 100%
    // until we actually know the page is ready.
    progress += (progressTarget - progress) * 0.06;
    setBar(progress);
    if (progress > progressTarget - 0.01 && progressTarget < 0.9) {
      progressTarget = Math.min(progressTarget + 0.16, 0.9);
    }

    renderer.render(scene, camera);
  }

  // Resize / rotate: the stage is sized in viewport units, so an orientation
  // change or an address bar collapsing on mobile changes the box the scene
  // has to fit. Coalesced into one rAF so a drag-resize doesn't thrash.
  var resizePending = false;
  function onResize() {
    if (resizePending) return;
    resizePending = true;
    requestAnimationFrame(function () {
      resizePending = false;
      sizeParticleCanvas();
      if (!renderer || !camera || !stage) return;
      renderer.setSize(stage.clientWidth || 320, stage.clientHeight || 320);
      fitCamera();
    });
  }

  function hidePreloader() {
    if (hidden) return;
    hidden = true;

    progressTarget = 1;
    setBar(1);

    if (rafId) cancelAnimationFrame(rafId);
    stopParticles();
    removeResizeListeners();

    root.classList.add('is-hidden');

    // Cue for whatever takes the screen next (the Season 1 gateway) to
    // start its own fade in, so the two cross-fade rather than stacking.
    // Dispatched as the fade begins, not when it finishes.
    try {
      document.dispatchEvent(new CustomEvent('cic:preloader-hidden'));
    } catch (e) {
      // Old browsers without the CustomEvent constructor — the gateway
      // polls the DOM as well, so it still picks this up.
    }

    var cleanup = function () {
      if (root.parentNode) root.parentNode.removeChild(root);
      if (renderer) {
        renderer.dispose();
        if (renderer.domElement && renderer.domElement.parentNode) {
          renderer.domElement.parentNode.removeChild(renderer.domElement);
        }
      }
      if (texture) texture.dispose();
      if (productGroup) {
        productGroup.children.forEach(function (m) {
          m.geometry.dispose();
          m.material.dispose();
        });
      }
      if (seasonGroup) {
        // Geometry is shared across every layer, so dispose it once.
        if (seasonGroup.children[0]) seasonGroup.children[0].geometry.dispose();
        seasonMaterials.forEach(function (mat) {
          if (mat.map) mat.map.dispose();
          mat.dispose();
        });
      }
    };
    root.addEventListener('transitionend', cleanup, { once: true });
    // Fallback in case transitionend never fires (e.g. display:none races)
    setTimeout(cleanup, 900);
  }

  function requestHide() {
    var elapsed = Date.now() - startTime;
    // Never fade out before the product's spin has actually finished settling
    // (skip that floor entirely under reduced motion, where it never spins).
    var rotationReady = prefersReducedMotion ? 0 : (ROTATION_DURATION + ROTATION_SETTLE_BUFFER);
    var minReady = Math.max(MIN_VISIBLE_MS, rotationReady);
    var wait = Math.max(minReady - elapsed, 0);
    setTimeout(hidePreloader, wait);
  }

  function addResizeListeners() {
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onResize);
    }
  }
  function removeResizeListeners() {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', onResize);
    }
  }

  // Safety net: never let the preloader block the site indefinitely.
  setTimeout(hidePreloader, MAX_VISIBLE_MS);

  if (document.readyState === 'complete') {
    requestHide();
  } else {
    window.addEventListener('load', requestHide);
  }
  // Preview-only hook (harmless in production: this event is never dispatched there)
  window.addEventListener('cic:preloader-test-load', requestHide);

  addResizeListeners();

  // Background particles run independently of the 3D scene, so they're up
  // instantly and still provide a background even if WebGL is unavailable.
  initParticles();

  if (!hasWebGL() || typeof THREE === 'undefined') {
    // No WebGL / three.js failed to load — don't strand the user on a blank screen.
    requestHide();
    return;
  }

  texture = new THREE.TextureLoader().load(
    IMAGE_URL,
    function (tex) {
      var img = tex.image;
      var aspect = (img && img.width && img.height) ? img.width / img.height : 0.87;
      tex.encoding = THREE.sRGBEncoding;
      tex.anisotropy = 4;
      initScene(aspect);
    },
    undefined,
    function () {
      // Image failed to load — fail gracefully instead of hanging.
      requestHide();
    }
  );
})();
